import React, { useState, useMemo } from 'react';
import { X, Clock, Search, ChevronDown, ChevronUp, RotateCcw, MessageSquare, Calendar, LayoutList, List, Printer } from 'lucide-react';
import { useRecentBills } from '../../../hooks/useRecentBills';
import ReprintReceiptModal from './ReprintReceiptModal';
import { P } from '@/config/permissionCodes';
import { useAuth } from '@/context/AuthContext';

export default function RecentOrdersModal({ currentTableId, onClose, onAddToCart, hideRepeatButton = false }) {
  // Simple filters
  const [daysFilter, setDaysFilter] = useState(1); // Default: Today
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'flat'

  const canReprint = useAuth().hasPermission(P.POS.DINE_IN.REPRINT);

  // Expandable state for bills
  const [expandedBills, setExpandedBills] = useState({});
  // Expandable state for orders within bills
  const [expandedOrders, setExpandedOrders] = useState({});

  // Reprint modal state
  const [reprintBill, setReprintBill] = useState(null);

  // Fetch recent bills from all tables
  const { recentBills, loading, error, refetch } = useRecentBills(
    null, // null = all tables
    50,   // Get last 50 bills
    daysFilter
  );

  // Display name — replace MAVERICK_COUNTER with Takeaway, same convention
  // used by ReceiptDisplay and PrintReceiptModal for counter-mode orders.
  const getDisplayTableName = (bill) =>
    bill.tableName?.toUpperCase().includes('MAVERICK_COUNTER')
      ? 'Takeaway'
      : bill.tableName || `Table ${bill.tableId}`;

  // Toggle bill expansion
  const toggleBill = (billId) => {
    setExpandedBills(prev => ({
      ...prev,
      [billId]: !prev[billId]
    }));
  };

  // Toggle order expansion within a bill
  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Filter bills by search query
  const filteredBills = useMemo(() => {
    if (!searchQuery.trim()) return recentBills;

    const query = searchQuery.toLowerCase();
    return recentBills.filter(bill => {
      // Search by bill number
      if (bill.billNumber?.toLowerCase().includes(query)) return true;

      // Search by table name
      if (bill.tableName?.toLowerCase().includes(query)) return true;

      // Search by items in orders
      const hasMatchingItem = bill.orders?.some(order =>
        order.items?.some(item =>
          item.itemName?.toLowerCase().includes(query) ||
          item.name?.toLowerCase().includes(query)
        )
      );
      if (hasMatchingItem) return true;

      return false;
    });
  }, [recentBills, searchQuery]);

  // Format timestamp - show full date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate stats
  const totalBills = filteredBills.length;

  // Handle repeat entire bill
  const handleRepeatBill = (bill) => {
    if (onAddToCart) {
      // Collect all items from all orders in this bill
      const allItems = [];
      bill.orders?.forEach(order => {
        order.items?.forEach(item => {
          allItems.push({
            id: item.menuItemId || item.id,
            name: item.itemName || item.name,
            price: item.unitPrice || item.price || 0,
            quantity: item.quantity || 1,
            variantId: item.variantId,
            variantName: item.variantName,
            customization: item.customization,
          });
        });
      });
      onAddToCart(allItems);
    }
  };

  // Handle repeat individual order
  const handleRepeatOrder = (order) => {
    if (onAddToCart) {
      const items = order.items.map(item => ({
        id: item.menuItemId || item.id,
        name: item.itemName || item.name,
        price: item.unitPrice || item.price || 0,
        quantity: item.quantity || 1,
        variantId: item.variantId,
        variantName: item.variantName,
        customization: item.customization,
      }));
      onAddToCart(items);
    }
  };

  // Get all items flat from a bill
  const getAllItemsFlat = (bill) => {
    const itemsMap = new Map();
    bill.orders?.forEach(order => {
      order.items?.forEach(item => {
        const key = `${item.menuItemId || item.id}-${item.variantId || 'no-variant'}`;
        if (itemsMap.has(key)) {
          const existing = itemsMap.get(key);
          existing.quantity += (item.quantity || 1);
          existing.totalPrice = existing.quantity * existing.price;
        } else {
          itemsMap.set(key, {
            id: item.menuItemId || item.id,
            name: item.itemName || item.name,
            price: item.unitPrice || item.price || 0,
            quantity: item.quantity || 1,
            totalPrice: (item.unitPrice || item.price || 0) * (item.quantity || 1),
            variantId: item.variantId,
            variantName: item.variantName,
          });
        }
      });
    });
    return Array.from(itemsMap.values());
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">

        {/* Clean Header */}
        <div className="bg-teal-600 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Recent Bills</h3>
              <p className="text-sm text-teal-100 mt-1">
                Quickly repeat previous bills and orders
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3 mb-3">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by item, table, or bill number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Days Filter */}
            <div className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 rounded-lg bg-white">
              <Calendar size={18} className="text-slate-500" />
              <select
                value={daysFilter}
                onChange={(e) => setDaysFilter(parseInt(e.target.value))}
                className="text-sm font-medium text-slate-700 focus:outline-none bg-transparent cursor-pointer"
              >
                <option value={1}>Today</option>
                <option value={2}>Yesterday</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          </div>

          {/* View Mode Toggle + Results count */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              {totalBills} bill{totalBills !== 1 ? 's' : ''} found
            </p>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grouped')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  viewMode === 'grouped'
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <LayoutList size={14} />
                <span>By Order</span>
              </button>
              <button
                onClick={() => setViewMode('flat')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  viewMode === 'flat'
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <List size={14} />
                <span>All Items</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {loading && (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
              <p className="mt-4 text-slate-600 font-medium">Loading bills...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-16">
              <p className="text-red-600 font-medium mb-4">{error}</p>
              <button
                onClick={refetch}
                className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && filteredBills.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={40} className="text-slate-400" />
              </div>
              <p className="text-slate-600 font-semibold text-lg">No bills found</p>
              <p className="text-slate-500 text-sm mt-2">
                {searchQuery ? 'Try adjusting your search' : 'No recent bills available'}
              </p>
            </div>
          )}

          {!loading && !error && filteredBills.length > 0 && (
            <div className="space-y-3">
              {filteredBills.map((bill) => {
                const isExpanded = expandedBills[bill.id];
                const totalOrders = bill.orders?.length || 0;
                const flatItems = getAllItemsFlat(bill);
                const displayTableName = getDisplayTableName(bill);

                return (
                  <div
                    key={bill.id}
                    className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all"
                  >
                    {/* Bill Header */}
                    <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold shadow-sm">
                            <span className="text-xs">BILL</span>
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 text-lg">
                              {bill.billNumber}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                              <span>{displayTableName}</span>
                              {bill.tableSplit && (
                                <>
                                  <span>•</span>
                                  <span>Split {bill.tableSplit}</span>
                                </>
                              )}
                              <span>•</span>
                              <Clock size={12} />
                              <span>{formatDateTime(bill.createdAt)}</span>
                              <span>•</span>
                              <span>{totalOrders} {totalOrders === 1 ? 'order' : 'orders'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-2xl font-black text-teal-700">
                              ₹{bill.finalAmount?.toFixed(0)}
                            </p>
                          </div>
                          {canReprint && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setReprintBill(bill);
                            }}
                            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-all hover:scale-105 flex items-center gap-2 shadow-md"
                            title="Print receipt"
                          >
                            <Printer size={18} />
                            <span>Print</span>
                          </button>
                          )}
                          {!hideRepeatButton && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRepeatBill(bill);
                              }}
                              className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg transition-all hover:scale-105 flex items-center gap-2 shadow-md"
                              title="Repeat entire bill"
                            >
                              <RotateCcw size={18} />
                              <span>Repeat</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Bill Notes */}
                      {bill.notes && (
                        <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-xs text-amber-800 font-medium italic">
                            "{bill.notes}"
                          </p>
                        </div>
                      )}

                      {/* Toggle Button */}
                      <button
                        onClick={() => toggleBill(bill.id)}
                        className="w-full mt-3 pt-3 border-t border-slate-200 flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 hover:text-teal-600 transition-colors"
                      >
                        <span>{isExpanded ? 'Hide' : 'View'} {viewMode === 'grouped' ? 'Orders' : 'Items'}</span>
                        {isExpanded ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>
                    </div>

                    {/* Expandable Bill Content */}
                    {isExpanded && (
                      <div className="bg-white">
                        {viewMode === 'grouped' ? (
                          // Grouped by Order View
                          <div className="p-4 space-y-3">
                            {bill.orders?.map((order) => {
                              const isOrderExpanded = expandedOrders[order.id];
                              const orderTotal = order.items?.reduce((sum, item) =>
                                sum + ((item.unitPrice || item.price || 0) * (item.quantity || 1)), 0
                              ) || 0;

                              return (
                                <div key={order.id} className="border border-slate-200 rounded-lg overflow-hidden">
                                  {/* Order Header */}
                                  <button
                                    onClick={() => toggleOrder(order.id)}
                                    className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-200"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="text-left">
                                          <p className="font-bold text-slate-900">
                                            {order.orderNumber || order.kotNumber}
                                          </p>
                                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                            <Clock size={12} />
                                            <span>{formatDateTime(order.createdAt)}</span>
                                            <span>•</span>
                                            <span>{order.items?.length || 0} items</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-3">
                                        {!hideRepeatButton && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRepeatOrder(order);
                                            }}
                                            className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg transition-all hover:scale-105 flex items-center gap-1.5"
                                            title="Repeat this order"
                                          >
                                            <RotateCcw size={14} />
                                            <span>Repeat</span>
                                          </button>
                                        )}
                                        <div className="text-right mr-2">
                                          <span className="text-sm font-bold text-slate-900">
                                            ₹{orderTotal.toFixed(0)}
                                          </span>
                                        </div>
                                        {isOrderExpanded ? (
                                          <ChevronUp size={18} className="text-slate-400" />
                                        ) : (
                                          <ChevronDown size={18} className="text-slate-400" />
                                        )}
                                      </div>
                                    </div>
                                  </button>

                                  {/* Order Items */}
                                  {isOrderExpanded && (
                                    <div className="bg-white">
                                      {order.items?.map((item, itemIndex) => (
                                        <div
                                          key={`${order.id}-${item.id}-${itemIndex}`}
                                          className="px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                                        >
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <h4 className="font-semibold text-slate-900">
                                                  {item.itemName || item.name}
                                                </h4>
                                                {item.variantName && (
                                                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded font-medium">
                                                    {item.variantName}
                                                  </span>
                                                )}
                                              </div>
                                              {item.customization?.customInstructions && (
                                                <p className="text-xs text-amber-700 flex items-start gap-1 mt-1.5">
                                                  <MessageSquare size={12} className="flex-shrink-0 mt-0.5" />
                                                  <span className="italic">"{item.customization.customInstructions}"</span>
                                                </p>
                                              )}
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                              <p className="text-xs text-slate-500 mb-0.5">
                                                {item.quantity || 1} × ₹{(item.unitPrice || item.price || 0).toFixed(0)}
                                              </p>
                                              <p className="font-bold text-slate-900">
                                                ₹{((item.unitPrice || item.price || 0) * (item.quantity || 1)).toFixed(0)}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          // Flat Items View
                          <div className="p-4">
                            {flatItems.map((item, idx) => (
                              <div
                                key={idx}
                                className="px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors rounded-lg mb-2"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-semibold text-slate-900">
                                        {item.name}
                                      </h4>
                                      {item.variantName && (
                                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded font-medium">
                                          {item.variantName}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-slate-500 mb-0.5">
                                      {item.quantity} × ₹{item.price.toFixed(0)}
                                    </p>
                                    <p className="font-bold text-slate-900">
                                      ₹{item.totalPrice.toFixed(0)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-white">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>

      {/* Reprint Receipt Modal */}
      {reprintBill && (
        <ReprintReceiptModal
          bill={reprintBill}
          onClose={() => setReprintBill(null)}
        />
      )}
    </div>
  );
}